import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  Home,
  Info,
  Loader,
  Moon,
  Pill,
  Shield,
  Sun,
  Sunrise,
  Sunset,
  Utensils,
} from 'lucide-react';
import { getPrescription } from '../services/firebaseService';
import type { DrugInteraction, Medicine, PrescriptionAnalysis, SafetyIssue } from '../types';
import '../styles/QRPrescriptionSchedule.css';

type TimeTab = 'morning' | 'noon' | 'evening' | 'night' | 'all';
type GroupKey = Exclude<TimeTab, 'all'> | 'asNeeded';

const QR_SCHEDULE_STORAGE_PREFIX = 'medcheck_qr_schedule_';

const TIME_TABS: Array<{ key: TimeTab; label: string; title: string; icon: ReactNode }> = [
  { key: 'morning', label: 'Morning', title: 'Morning Medicines', icon: <Sunrise size={18} /> },
  { key: 'noon', label: 'Noon', title: 'Noon Medicines', icon: <Sun size={18} /> },
  { key: 'evening', label: 'Evening', title: 'Evening Medicines', icon: <Sunset size={18} /> },
  { key: 'night', label: 'Night', title: 'Night Medicines', icon: <Moon size={18} /> },
  { key: 'all', label: 'All', title: 'All Medicines', icon: <Pill size={18} /> },
];

function getDefaultTimeTab(date = new Date()): TimeTab {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 16) return 'noon';
  if (hour >= 16 && hour < 20) return 'evening';
  return 'night';
}

function displayValue(value?: string, placeholder = 'Not specified'): string {
  return value && value.trim() ? value : placeholder;
}

function getSearchText(medicine: Medicine): string {
  return [
    medicine.name,
    medicine.frequency,
    medicine.frequencyLabel,
    medicine.timing,
    medicine.timingLabel,
    medicine.duration,
    medicine.durationLabel,
    medicine.instruction,
    medicine.englishInstruction,
    medicine.rawInstruction,
    medicine.notes,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function normalizeBanglaDigits(text: string): string {
  return text.replace(/[\u09E6-\u09EF]/g, (digit) => String(digit.charCodeAt(0) - 0x09E6));
}

function parseThreePartFrequency(text: string): number[] | null {
  const match = normalizeBanglaDigits(text).match(/([0-9]+)\s*\+\s*([0-9]+)\s*\+\s*([0-9]+)/);
  return match ? match.slice(1).map((value) => Number(value)) : null;
}

function getMedicineTimeTabs(medicine: Medicine): TimeTab[] {
  const text = getSearchText(medicine);
  const tabs = new Set<TimeTab>();
  const frequency = parseThreePartFrequency(text);

  if (frequency) {
    if (frequency[0] > 0) tabs.add('morning');
    if (frequency[1] > 0) tabs.add('noon');
    if (frequency[2] > 0) tabs.add('night');
  }

  if (/morning|breakfast|\u09B8\u0995\u09BE\u09B2|\u09A8\u09BE\u09B8\u09CD\u09A4\u09BE/.test(text)) tabs.add('morning');
  if (/noon|lunch|\u09A6\u09C1\u09AA\u09C1\u09B0/.test(text)) tabs.add('noon');
  if (/evening|\u09B8\u09A8\u09CD\u09A7\u09CD\u09AF\u09BE|\u09AC\u09BF\u0995\u09BE\u09B2/.test(text)) tabs.add('evening');
  if (/night|bedtime|\u09B0\u09BE\u09A4|\u09B0\u09BE\u09A4\u09C7/.test(text)) tabs.add('night');
  if (/every\s*8\s*hours?|8\s*hours?\s*apart/.test(text)) {
    tabs.add('morning');
    tabs.add('noon');
    tabs.add('evening');
    tabs.add('night');
  }
  if (/every\s*12\s*hours?|12\s*hours?\s*apart/.test(text)) {
    tabs.add('morning');
    tabs.add('night');
  }

  return Array.from(tabs);
}

function isValidPrescription(value: unknown): value is PrescriptionAnalysis {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as PrescriptionAnalysis;
  return Array.isArray(candidate.medicines) && candidate.medicines.length > 0;
}

function getSafetyStatus(prescription: PrescriptionAnalysis): string {
  const confirmedRisk =
    prescription.interactions?.some((interaction) =>
      interaction.status === 'confirmed_interaction' &&
      (interaction.severity === 'critical' || interaction.severity === 'high')
    ) ||
    prescription.issues?.some((issue) =>
      issue.status === 'confirmed_interaction' &&
      (issue.severity === 'critical' || issue.severity === 'high')
    );

  if (confirmedRisk) return 'High Risk';
  if ((prescription.warnings || []).length > 0 || (prescription.issues || []).length > 0) return 'Needs Review';
  return 'No confirmed dangerous interaction found';
}

function getWarnings(prescription: PrescriptionAnalysis): Array<{ tone: 'critical' | 'caution'; text: string }> {
  const warnings: Array<{ tone: 'critical' | 'caution'; text: string }> = [];

  prescription.medicines
    .filter((medicine) => medicine.identificationStatus !== 'identified')
    .forEach((medicine) => {
      warnings.push({ tone: 'caution', text: `${medicine.name}: needs review or active ingredient verification.` });
    });

  prescription.medicines
    .filter((medicine) => !medicine.duration || !medicine.frequency)
    .forEach((medicine) => {
      warnings.push({ tone: 'caution', text: `${medicine.name}: missing duration or frequency.` });
    });

  (prescription.interactions || []).forEach((interaction: DrugInteraction) => {
    warnings.push({
      tone:
        interaction.status === 'confirmed_interaction' &&
        (interaction.severity === 'critical' || interaction.severity === 'high')
          ? 'critical'
          : 'caution',
      text: `${interaction.drug1} + ${interaction.drug2}: ${interaction.description}`,
    });
  });

  (prescription.issues || []).forEach((issue: SafetyIssue) => {
    warnings.push({
      tone:
        issue.status === 'confirmed_interaction' &&
        (issue.severity === 'critical' || issue.severity === 'high')
          ? 'critical'
          : 'caution',
      text: `${issue.title}: ${issue.description}`,
    });
  });

  return warnings;
}

async function loadPrescriptionById(reportId: string): Promise<PrescriptionAnalysis | null> {
  const localValue = localStorage.getItem(`${QR_SCHEDULE_STORAGE_PREFIX}${reportId}`);
  if (localValue) {
    try {
      const parsed = JSON.parse(localValue);
      if (isValidPrescription(parsed)) return parsed;
    } catch {
      localStorage.removeItem(`${QR_SCHEDULE_STORAGE_PREFIX}${reportId}`);
    }
  }

  return getPrescription(reportId);
}

function groupMedicines(medicines: Medicine[]): Record<GroupKey, Medicine[]> {
  const groups: Record<GroupKey, Medicine[]> = {
    morning: [],
    noon: [],
    evening: [],
    night: [],
    asNeeded: [],
  };

  medicines.forEach((medicine) => {
    const tabs = getMedicineTimeTabs(medicine);
    if (tabs.length === 0) {
      groups.asNeeded.push(medicine);
      return;
    }

    tabs.forEach((tab) => {
      if (tab !== 'all') groups[tab].push(medicine);
    });
  });

  return groups;
}

export const QRPrescriptionSchedule: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TimeTab>(getDefaultTimeTab());
  const [prescription, setPrescription] = useState<PrescriptionAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const reportId = searchParams.get('id') || searchParams.get('reportId') || searchParams.get('rx');

  useEffect(() => {
    const load = async () => {
      if (!reportId) {
        setError(true);
        setLoading(false);
        return;
      }

      try {
        const loaded = await loadPrescriptionById(reportId);
        if (loaded && isValidPrescription(loaded)) {
          setPrescription(loaded);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [reportId]);

  const groupedMedicines = useMemo(() => groupMedicines(prescription?.medicines || []), [prescription]);

  if (loading) {
    return (
      <main className="qr-schedule-page">
        <div className="qr-schedule-state">
          <Loader className="spin" size={34} />
          <h1>Loading prescription schedule</h1>
          <p>Please wait while MedCheck opens your saved prescription.</p>
        </div>
      </main>
    );
  }

  if (error || !prescription) {
    return (
      <main className="qr-schedule-page">
        <div className="qr-schedule-state error">
          <AlertTriangle size={38} />
          <h1>Prescription data not found.</h1>
          <p>Please generate a new QR code from MedCheck.</p>
          <button onClick={() => navigate('/upload')}>
            <Home size={18} />
            Back to MedCheck
          </button>
        </div>
      </main>
    );
  }

  const activeConfig = TIME_TABS.find((tab) => tab.key === activeTab) || TIME_TABS[0];
  const activeMedicines = activeTab === 'all' ? prescription.medicines : groupedMedicines[activeTab];
  const identifiedCount = prescription.medicines.filter((medicine) => medicine.identificationStatus === 'identified').length;
  const reviewCount = prescription.medicines.length - identifiedCount;
  const warnings = getWarnings(prescription);

  return (
    <main className="qr-schedule-page">
      <section className="qr-schedule-hero">
        <div>
          <span className="qr-kicker"><Shield size={15} /> MedCheck QR Schedule</span>
          <h1>Medicines to take now</h1>
          {prescription.patientInfo?.patientName && (
            <div className="qr-patient-line">
              <span>{prescription.patientInfo.patientName}</span>
              {prescription.patientInfo.age && <span>Age {prescription.patientInfo.age}</span>}
              {prescription.patientInfo.date && <span>{prescription.patientInfo.date}</span>}
            </div>
          )}
          <p>{activeConfig.title}. Follow the original prescription and confirm unclear instructions with a doctor or pharmacist.</p>
        </div>
        <div className="qr-hero-score">
          <strong>{Math.round(prescription.safetyScore || 0)}</strong>
          <span>Safety score</span>
        </div>
      </section>

      <nav className="time-tabs" aria-label="Medicine time tabs">
        {TIME_TABS.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      <section className="now-panel">
        <SectionHeading icon={<Clock3 size={20} />} title={activeConfig.title} subtitle="Medicines scheduled for the selected time." />
        {activeMedicines.length > 0 ? (
          <div className="now-medicine-grid">
            {activeMedicines.map((medicine, index) => (
              <motion.article className="now-medicine-card" key={`${medicine.name}-${index}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="medicine-card-index">{index + 1}</div>
                <div>
                  <h3>{displayValue(medicine.name, 'Needs review')}</h3>
                  <div className="medicine-minimal-details">
                    <span><Pill size={15} /> Dose: {displayValue(medicine.dosage, 'Needs review')}</span>
                    <span><Utensils size={15} /> Food timing: {displayValue(medicine.timingLabel || medicine.timing, 'Not specified')}</span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="qr-empty-state">
            <CheckCircle2 size={34} />
            <h3>No medicine scheduled for this time.</h3>
            <p>Switch tabs to review other medicine times from this prescription.</p>
          </div>
        )}
      </section>

      <section className="qr-report-grid">
        <ReportCard icon={<FileText size={20} />} title="Prescription Summary">
          <div className="summary-grid">
            <SummaryMetric label="Total medicines" value={prescription.medicines.length} />
            <SummaryMetric label="Identified" value={identifiedCount} />
            <SummaryMetric label="Need review" value={reviewCount} />
            <SummaryMetric label="Safety status" value={getSafetyStatus(prescription)} text />
          </div>
        </ReportCard>

        <ReportCard icon={<CalendarDays size={20} />} title="Full Medicine List">
          <GroupedMedicineList groups={groupedMedicines} />
        </ReportCard>

        <ReportCard icon={<Info size={20} />} title="Important Instructions">
          <ul className="qr-info-list">
            <li>Food timing: follow the timing shown for each medicine. If missing, confirm with a doctor or pharmacist.</li>
            <li>Duration: continue only for the duration written in the original prescription.</li>
            <li>Missed dose: ask a pharmacist or doctor what to do. Do not double a dose unless instructed.</li>
            <li>Do not stop prescribed medicines without doctor advice.</li>
          </ul>
        </ReportCard>

        <ReportCard icon={<Shield size={20} />} title="Suggestions">
          <ul className="qr-info-list">
            <li>Keep medicine strips or boxes for verification.</li>
            <li>Confirm unclear medicines with a doctor or pharmacist.</li>
            <li>Follow the original prescription exactly.</li>
          </ul>
        </ReportCard>

        <ReportCard icon={<AlertTriangle size={20} />} title="Warnings">
          {warnings.length > 0 ? (
            <div className="warning-list">
              {warnings.map((warning, index) => (
                <div className={`warning-row ${warning.tone}`} key={`${warning.text}-${index}`}>
                  <AlertTriangle size={16} />
                  <span>{warning.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-warning">No confirmed dangerous interaction found in this saved report.</p>
          )}
        </ReportCard>
      </section>

      <section className="qr-disclaimer">
        <Info size={18} />
        <p>MedCheck does not diagnose, prescribe, or replace a doctor or pharmacist. Use this QR schedule as a patient guidance aid and confirm all uncertain medicine details with a licensed professional.</p>
      </section>
    </main>
  );
};

const SectionHeading: React.FC<{ icon: ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div className="section-heading">
    {icon}
    <div>
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
  </div>
);

const ReportCard: React.FC<{ icon: ReactNode; title: string; children: ReactNode }> = ({ icon, title, children }) => (
  <section className="qr-report-card">
    <SectionHeading icon={icon} title={title} subtitle="" />
    {children}
  </section>
);

const SummaryMetric: React.FC<{ label: string; value: string | number; text?: boolean }> = ({ label, value, text }) => (
  <div className={`summary-metric ${text ? 'text' : ''}`}>
    <strong>{value}</strong>
    <span>{label}</span>
  </div>
);

const GroupedMedicineList: React.FC<{ groups: Record<GroupKey, Medicine[]> }> = ({ groups }) => {
  const config: Array<{ key: GroupKey; label: string }> = [
    { key: 'morning', label: 'Morning' },
    { key: 'noon', label: 'Noon' },
    { key: 'evening', label: 'Evening' },
    { key: 'night', label: 'Night' },
    { key: 'asNeeded', label: 'As Needed' },
  ];

  return (
    <div className="grouped-list">
      {config.map((group) => (
        <div className="grouped-list-section" key={group.key}>
          <h3>{group.label}</h3>
          {groups[group.key].length > 0 ? (
            groups[group.key].map((medicine, index) => (
              <div className="grouped-medicine-row" key={`${group.key}-${medicine.name}-${index}`}>
                <span>{displayValue(medicine.name, 'Needs review')}</span>
                <small>{displayValue(medicine.dosage, 'Needs review')} | {displayValue(medicine.timingLabel || medicine.timing, 'Not specified')}</small>
              </div>
            ))
          ) : (
            <p>No medicine scheduled for this time.</p>
          )}
        </div>
      ))}
    </div>
  );
};
